import { useState, useEffect } from "react";
import axios from "axios";
import { CreditCard, Phone, ArrowLeft, Star, DollarSign, Calendar, Building2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TIERS = {
  premium:  { label: "Premium",  glow: "rgba(34,211,238,0.25)", color: "#22d3ee" },
  standard: { label: "Standard", glow: "rgba(192,132,252,0.25)", color: "#c084fc" },
  value:    { label: "Value",    glow: "rgba(74,222,128,0.25)", color: "#4ade80" },
  starter:  { label: "Starter",  glow: "rgba(250,204,21,0.25)", color: "#facc15" },
};

const fmtMoney = (n) => {
  const num = parseFloat(n);
  if (isNaN(num)) return n || "—";
  return "$" + num.toLocaleString("en-US", { maximumFractionDigits: 0 });
};

export default function PublicTradelinesPage() {
  const [tradelines, setTradelines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/tradelines`)
      .then(r => setTradelines((r.data?.tradelines || []).filter(t => t.is_active !== false)))
      .catch(() => setTradelines([]))
      .finally(() => setLoading(false));
  }, []);

  // Sort: featured first, then by limit desc
  const sorted = [...tradelines].sort((a, b) => {
    if (a.is_featured && !b.is_featured) return -1;
    if (!a.is_featured && b.is_featured) return 1;
    return (parseFloat(b.limit) || 0) - (parseFloat(a.limit) || 0);
  });

  return (
    <div data-testid="public-tradelines-page" style={styles.page}>
      <div style={styles.gridBg} />
      <div style={{ ...styles.orb, ...styles.orb1 }} />
      <div style={{ ...styles.orb, ...styles.orb2 }} />

      <header style={styles.header}>
        <a href="/" style={styles.brand} data-testid="back-home-link">
          <div style={styles.brandMark}>C</div>
          <span>Clear All Credit</span>
        </a>
        <a href="tel:6198820259" style={styles.callBtn} data-testid="call-btn">
          <Phone size={14} /> (619) 882-0259
        </a>
      </header>

      <main style={styles.main}>
        <a href="https://www.clearallcreditrepairs.com" style={styles.backLink}>
          <ArrowLeft size={16} /> Back to website
        </a>

        <div style={styles.tag}>PREMIUM TRADELINES</div>
        <h1 style={styles.h1}>Authorized User <span style={styles.gold}>Tradelines</span></h1>
        <p style={styles.lead}>
          Our hand-picked, seasoned credit lines available for authorized user placement. Boost your credit profile with high-limit, perfect-payment-history accounts that report to all three bureaus.
        </p>

        <div style={styles.legend}>
          {Object.entries(TIERS).map(([key, t]) => (
            <div key={key} style={{ ...styles.legendChip, color: t.color, borderColor: t.color, background: t.glow }}>
              {t.label}
            </div>
          ))}
        </div>

        {loading && <div style={styles.loading}>Loading tradelines…</div>}

        {!loading && sorted.length === 0 && (
          <div style={styles.empty}>
            <CreditCard size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
            <p>New tradelines are being added. Call us at (619) 882-0259 for the latest inventory.</p>
          </div>
        )}

        <div style={styles.grid}>
          {sorted.map((t, i) => {
            const tier = TIERS[t.tier] || TIERS.value;
            return (
              <div
                key={t.id || i}
                style={{ ...styles.card, boxShadow: t.is_featured ? `0 0 60px ${tier.glow}` : "none", borderColor: t.is_featured ? tier.color : "rgba(255,255,255,0.08)" }}
                data-testid={`tradeline-${t.id || i}`}
              >
                {t.is_featured && (
                  <div style={styles.featured}>
                    <Star size={12} fill="#0a0f24" /> FEATURED
                  </div>
                )}
                <div style={styles.cardTop}>
                  <div style={{ ...styles.tierBadge, color: tier.color, borderColor: tier.color, background: tier.glow }}>
                    {tier.label}
                  </div>
                  <div style={styles.cardBank}>
                    <Building2 size={14} style={{ opacity: 0.6 }} />
                    <span>{t.name}</span>
                  </div>
                </div>

                <div style={styles.limitBlock}>
                  <div style={styles.limitLabel}>Credit Limit</div>
                  <div style={styles.limitValue}>{fmtMoney(t.limit)}</div>
                </div>

                <div style={styles.metrics}>
                  <div style={styles.metric}>
                    <Calendar size={14} style={{ opacity: 0.6 }} />
                    <div>
                      <div style={styles.metricLabel}>Opened</div>
                      <div style={styles.metricValue}>{t.opened_date || "—"}</div>
                    </div>
                  </div>
                  <div style={styles.metric}>
                    <Calendar size={14} style={{ opacity: 0.6 }} />
                    <div>
                      <div style={styles.metricLabel}>Posts on</div>
                      <div style={styles.metricValue}>Day {t.posting_day || "—"}</div>
                    </div>
                  </div>
                </div>

                <div style={styles.priceRow}>
                  <div>
                    <div style={styles.priceLabel}>Price</div>
                    <div style={styles.priceValue}><DollarSign size={18} style={{ display: "inline", marginRight: 2 }} />{fmtMoney(t.price).replace("$","")}</div>
                  </div>
                  <a href="tel:6198820259" style={{ ...styles.reserveBtn, background: tier.color, color: "#0a0f24" }} data-testid={`reserve-${t.id || i}`}>
                    Reserve
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        <div style={styles.cta}>
          <h2 style={styles.ctaTitle}>Ready to add a tradeline to your file?</h2>
          <p style={styles.ctaText}>Reserve your spot before the next posting cycle. Tradelines fill up fast — call now for guaranteed placement on your preferred report date.</p>
          <div style={styles.ctaBtns}>
            <a href="tel:6198820259" style={styles.btnPrimary} data-testid="cta-call">
              <Phone size={18} /> Call (619) 882-0259
            </a>
            <a href="https://pdf-gen-preview-1.emergent.host/client-intake" target="_blank" rel="noopener noreferrer" style={styles.btnSecondary} data-testid="cta-intake">
              Start Free Intake →
            </a>
          </div>
        </div>

        <p style={styles.disclaimer}>
          *Authorized user tradelines are a credit-building tool, not credit repair. Posting dates are estimates and depend on the issuing bank's reporting schedule. Past performance does not guarantee future results.
        </p>
      </main>

      <footer style={styles.footer}>
        © 2026 Clear All Credit Repairs · CROA Compliant · San Diego, CA
      </footer>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "#050816", color: "#e7ecff", fontFamily: "'Inter', sans-serif", position: "relative", overflowX: "hidden" },
  gridBg: { position: "fixed", inset: 0, backgroundImage: "linear-gradient(rgba(111,139,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(111,139,255,0.05) 1px, transparent 1px)", backgroundSize: "60px 60px", maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)", pointerEvents: "none" },
  orb: { position: "fixed", borderRadius: "50%", filter: "blur(120px)", pointerEvents: "none", zIndex: 0 },
  orb1: { width: 500, height: 500, background: "#22d3ee", top: -180, left: -180, opacity: 0.25 },
  orb2: { width: 500, height: 500, background: "#ffc857", bottom: -180, right: -180, opacity: 0.2 },
  header: { position: "sticky", top: 0, zIndex: 50, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", background: "rgba(5,8,22,0.7)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.08)" },
  brand: { display: "flex", alignItems: "center", gap: 10, fontWeight: 800, color: "#e7ecff", textDecoration: "none" },
  brandMark: { width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#ffc857,#ffb627)", color: "#0a0f24", display: "grid", placeItems: "center", fontWeight: 800, boxShadow: "0 0 30px rgba(255,200,87,0.4)" },
  callBtn: { display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 18px", background: "linear-gradient(135deg,#ffc857,#ffb627)", color: "#0a0f24", fontWeight: 700, borderRadius: 999, fontSize: 13, textDecoration: "none" },
  main: { maxWidth: 1200, margin: "0 auto", padding: "60px 24px", position: "relative", zIndex: 2 },
  backLink: { display: "inline-flex", alignItems: "center", gap: 8, color: "#8b93b3", fontSize: 14, marginBottom: 24, textDecoration: "none" },
  tag: { display: "inline-block", padding: "6px 14px", borderRadius: 999, background: "rgba(34,211,238,0.1)", color: "#22d3ee", fontSize: 12, fontWeight: 600, letterSpacing: "0.1em", marginBottom: 16, border: "1px solid rgba(34,211,238,0.2)" },
  h1: { fontFamily: "'Sora', sans-serif", fontSize: "clamp(36px,6vw,68px)", fontWeight: 800, lineHeight: 1.1, marginBottom: 16, letterSpacing: "-0.02em" },
  gold: { background: "linear-gradient(135deg,#ffc857,#ff8a00)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" },
  lead: { color: "#8b93b3", fontSize: 18, maxWidth: 720, marginBottom: 32, lineHeight: 1.6 },
  legend: { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 40 },
  legendChip: { padding: "6px 14px", borderRadius: 999, fontSize: 12, fontWeight: 600, border: "1px solid", letterSpacing: "0.05em" },
  loading: { color: "#8b93b3", padding: "60px 0", textAlign: "center" },
  empty: { textAlign: "center", padding: 60, color: "#8b93b3", borderRadius: 18, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 20 },
  card: { position: "relative", padding: 24, borderRadius: 22, background: "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)", border: "1px solid", transition: "transform 0.3s" },
  featured: { position: "absolute", top: -10, right: 16, padding: "4px 10px", background: "linear-gradient(135deg,#ffc857,#ffb627)", color: "#0a0f24", borderRadius: 999, fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", gap: 4, letterSpacing: "0.1em" },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  tierBadge: { padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, border: "1px solid", letterSpacing: "0.05em" },
  cardBank: { display: "flex", alignItems: "center", gap: 6, color: "#8b93b3", fontSize: 13, fontWeight: 600 },
  limitBlock: { padding: "20px 0", borderTop: "1px dashed rgba(255,255,255,0.08)", borderBottom: "1px dashed rgba(255,255,255,0.08)", marginBottom: 16 },
  limitLabel: { fontSize: 11, color: "#8b93b3", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 },
  limitValue: { fontFamily: "'Sora', sans-serif", fontSize: 36, fontWeight: 800, color: "#fff", letterSpacing: "-0.02em" },
  metrics: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 },
  metric: { display: "flex", alignItems: "center", gap: 8 },
  metricLabel: { fontSize: 10, color: "#8b93b3", textTransform: "uppercase", letterSpacing: "0.1em" },
  metricValue: { fontSize: 13, fontWeight: 600, color: "#e7ecff" },
  priceRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-end" },
  priceLabel: { fontSize: 11, color: "#8b93b3", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 },
  priceValue: { fontFamily: "'Sora', sans-serif", fontSize: 24, fontWeight: 800, color: "#ffc857" },
  reserveBtn: { padding: "10px 18px", fontWeight: 700, fontSize: 13, borderRadius: 10, textDecoration: "none", letterSpacing: "0.05em" },
  cta: { marginTop: 60, padding: "60px 32px", borderRadius: 28, background: "linear-gradient(135deg, rgba(34,211,238,0.08), rgba(111,139,255,0.08))", border: "1px solid rgba(34,211,238,0.2)", textAlign: "center" },
  ctaTitle: { fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: "clamp(26px,4vw,40px)", marginBottom: 12 },
  ctaText: { color: "#8b93b3", fontSize: 16, maxWidth: 540, margin: "0 auto 28px" },
  ctaBtns: { display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" },
  btnPrimary: { display: "inline-flex", alignItems: "center", gap: 8, padding: "16px 28px", background: "linear-gradient(135deg,#ffc857,#ffb627)", color: "#0a0f24", fontWeight: 700, borderRadius: 14, fontSize: 15, textDecoration: "none", boxShadow: "0 10px 40px rgba(255,200,87,0.25)" },
  btnSecondary: { display: "inline-flex", alignItems: "center", gap: 8, padding: "16px 28px", background: "rgba(255,255,255,0.06)", color: "#e7ecff", fontWeight: 600, borderRadius: 14, fontSize: 15, textDecoration: "none", border: "1px solid rgba(255,255,255,0.12)" },
  disclaimer: { marginTop: 40, fontSize: 12, color: "#6b7280", lineHeight: 1.6, fontStyle: "italic" },
  footer: { textAlign: "center", padding: "40px 24px", color: "#8b93b3", fontSize: 13, borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: 60, position: "relative", zIndex: 2 },
};
